import {
  ArticulationCreditList,
  CourseCreditList,
  PrecedentCreditList,
} from "components/credits/CreditList";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Col, Row, Switch } from "antd";
import { cloneDeep, get, isEmpty, isNil } from "lodash";
import {
  generalCreditSortOrder,
  getTotalCreditPoints,
  isArticulation,
} from "utils/creditUtils";
import {
  getCreditArrangementsMap,
  isFetchingCreditArrangements,
} from "store/credit-arrangement/creditArrangementSelector";
import {
  getSourceInstitutionsMap,
  isFetchingSourceInstitutions,
} from "store/source-institution/sourceInstitutionSelector";

import BackToResultButton from "common/ui/button/BackToResultButton";
import { CREDIT_TYPES } from "common/model/creditType";
import FullPageSpinner from "common/ui/loading/FullPageSpinner";
import NoSavedCredit from "./NoSavedCredit";
import React from "react";
import classes from "./SavedCreditItems.module.scss";
import { connect } from "react-redux";
import { getCreditBookmarks } from "store/credit-bookmark/creditBookmarkSelector";
import i from "i18next";
import { isApplicationFinishedLoadingSelector } from "utils/generalUtils";

class SavedCreditItems extends React.Component {
  state = {
    groupedByCourse: false,
  };

  changeGrouping = (checked) => {
    this.setState({ groupedByCourse: checked });
  };

  sortByCreditPoints = (a, b) => {
    const aToCompare = a.grouped
      ? a.grouped.sort(generalCreditSortOrder)[0]
      : a;
    const bToCompare = b.grouped
      ? b.grouped.sort(generalCreditSortOrder)[0]
      : b;
    if (a.grouped && !b.grouped) {
      return -1;
    }
    if (!a.grouped && b.grouped) {
      return 1;
    }
    return generalCreditSortOrder(aToCompare, bToCompare);
  };

  transfromBookmarksToTargetCourseMap = (
    creditBookmarks,
    creditArrangementsMap,
  ) => {
    const transformedBookmarks = {};
    const unspecifiedCredits = [];
    if (!creditBookmarks || isEmpty(creditArrangementsMap)) {
      return {};
    }
    creditBookmarks.forEach((creditBookMark) => {
      const { sourceInstitutionID } = creditBookMark;
      const creditArrangements = creditArrangementsMap[sourceInstitutionID];

      if (isArticulation(creditBookMark.creditType)) {
        const foundCredential = creditArrangements.articulations.find(
          (articulation) => articulation.ID === creditBookMark.creditItemID,
        );
        const courseID = foundCredential.course.ID;
        if (transformedBookmarks[courseID]) {
          transformedBookmarks[courseID].push({
            ...foundCredential,
            type: CREDIT_TYPES.ARTICULATION,
          });
        } else {
          transformedBookmarks[courseID] = [
            { ...foundCredential, type: CREDIT_TYPES.ARTICULATION },
          ];
        }
      } else {
        const precedents = creditArrangements.precedents || [];
        const foundPrecedent = precedents.find(
          (precedent) => precedent.ID === creditBookMark.creditItemID,
        );
        if (foundPrecedent.courses.length === 0) {
          //can assume that they are unspecified
          unspecifiedCredits.push({
            ...foundPrecedent,
            type: CREDIT_TYPES.PRECEDENT,
          });
        }
        foundPrecedent.courses.forEach((course) => {
          if (transformedBookmarks[course.ID]) {
            transformedBookmarks[course.ID].push({
              ...foundPrecedent,
              type: CREDIT_TYPES.PRECEDENT,
              course: course,
            });
          } else {
            transformedBookmarks[course.ID] = [
              {
                ...foundPrecedent,
                type: CREDIT_TYPES.PRECEDENT,
                course: course,
              },
            ];
          }
        });
      }
    });
    //add the unspecified credits to each
    Object.keys(transformedBookmarks).forEach((id) => {
      transformedBookmarks[id].push(...unspecifiedCredits);
    });
    return transformedBookmarks;
  };

  transformBookmarksToSimpleCreditItemsMap = (
    creditBookmarks,
    creditArrangementsMap,
  ) => {
    if (!creditBookmarks || !creditArrangementsMap) {
      return {};
    }
    const creditItemsMap = {};
    creditBookmarks.forEach((creditBookMark) => {
      const { sourceInstitutionID } = creditBookMark;
      const creditArrangements = creditArrangementsMap[sourceInstitutionID];
      if (isEmpty(creditArrangements)) {
        return;
      } else {
        if (isArticulation(creditBookMark.creditType)) {
          const articulations = creditArrangements.articulations || [];
          const foundArticulation = articulations.find(
            (articulation) => articulation.ID === creditBookMark.creditItemID,
          );
          if (creditItemsMap[sourceInstitutionID]) {
            creditItemsMap[sourceInstitutionID].push({
              ...foundArticulation,
              type: CREDIT_TYPES.ARTICULATION,
            });
          } else {
            creditItemsMap[sourceInstitutionID] = [
              {
                ...foundArticulation,
                type: CREDIT_TYPES.ARTICULATION,
              },
            ];
          }
        } else {
          const precedents = creditArrangements.precedents || [];
          const foundPrecedent = precedents.find(
            (precedent) => precedent.ID === creditBookMark.creditItemID,
          );
          if (creditItemsMap[sourceInstitutionID]) {
            creditItemsMap[sourceInstitutionID].push({
              ...foundPrecedent,
              type: CREDIT_TYPES.PRECEDENT,
            });
          } else {
            creditItemsMap[sourceInstitutionID] = [
              {
                ...foundPrecedent,
                type: CREDIT_TYPES.PRECEDENT,
              },
            ];
          }
        }
      }
    });
    const sortedCreditItemsMap = {};
    Object.keys(creditItemsMap).forEach((ck) => {
      const items = cloneDeep(creditItemsMap[ck]);
      items.sort(generalCreditSortOrder);
      sortedCreditItemsMap[ck] = items;
    });
    return sortedCreditItemsMap;
  };

  transformBookmarksToCreditItemsMap = (
    creditBookmarks,
    creditArrangementsMap,
  ) => {
    //checks if it should be grouped
    if (isEmpty(creditArrangementsMap)) {
      return [];
    }
    const creditItemsMap = {};
    //creditItemsMap has the sourceinstitutionid
    const groupedIndex = {};
    //contains a dictionary of the indexes of each of the grouped credit blocks within their respective creditItemsMap[sourceInstitution]
    creditBookmarks.forEach((bookmark) => {
      const { sourceInstitutionID } = bookmark;
      const creditArrangements = creditArrangementsMap[sourceInstitutionID];
      if (isEmpty(creditArrangements)) {
        return {};
      }
      if (isArticulation(bookmark.creditType)) {
        const articulations = creditArrangements.articulations || [];
        const foundArticulation = articulations.find(
          (articulation) => articulation.ID === bookmark.creditItemID,
        );
        if (!foundArticulation) {
          console.info("No found articulation");
          return {};
        }
        const credentialIssuerID = get(
          foundArticulation.credentials[0],
          "data.credentialIssuerID",
        );
        let key = credentialIssuerID;
        foundArticulation.credentials.forEach((credential) => {
          const courseName = get(credential, "data.unitName");
          key += "-" + courseName;
        });
        const targetIndex = groupedIndex[key];

        if (isNil(targetIndex)) {
          creditItemsMap[sourceInstitutionID] =
            creditItemsMap[sourceInstitutionID] || [];
          groupedIndex[key] = creditItemsMap[sourceInstitutionID].length;
          creditItemsMap[sourceInstitutionID].push({
            grouped: [
              { ...foundArticulation, type: CREDIT_TYPES.ARTICULATION },
            ],
          });
        } else {
          creditItemsMap[sourceInstitutionID][targetIndex].grouped.push({
            ...foundArticulation,
            type: CREDIT_TYPES.ARTICULATION,
          });
        }
      } else {
        const precedents = creditArrangements.precedents || [];
        const foundPrecedent = precedents.find(
          (precedent) => precedent.ID === bookmark.creditItemID,
        );
        if (creditItemsMap[sourceInstitutionID]) {
          creditItemsMap[sourceInstitutionID].push(foundPrecedent);
        } else {
          creditItemsMap[sourceInstitutionID] = [foundPrecedent];
        }
      }
    });

    const sortedCreditItemsMap = {};

    Object.keys(creditItemsMap).forEach((ck) => {
      const items = cloneDeep(creditItemsMap[ck]);
      items.sort((a, b) => {
        let aItem = a.grouped ? a.grouped[0] : a;
        let bItem = b.grouped ? b.grouped[0] : b;
        return generalCreditSortOrder(aItem, bItem);
      });
      sortedCreditItemsMap[ck] = items;
    });
    return sortedCreditItemsMap;
  };

  sortForUniGroups = (a, b, creditMap) => {
    // Precedents with highest credit points

    const aItems = creditMap[a].sort(this.sortByCreditPoints);
    const bItems = creditMap[b].sort(this.sortByCreditPoints);

    if (isEmpty(aItems) || isEmpty(bItems)) return 0;

    const firstAItem = aItems[0];
    const firstBItem = bItems[0];

    return generalCreditSortOrder(firstAItem, firstBItem);
  };

  sortInsideTargetCourseMap = (targetCourseMap) => {
    if (isEmpty(targetCourseMap)) return;
    Object.keys(targetCourseMap).forEach((courseKey) => {
      const block = targetCourseMap[courseKey];
      const newBlock = block.sort(generalCreditSortOrder);
      targetCourseMap[courseKey] = newBlock;
    });
  };

  renderSavedItems = () => {
    const {
      creditBookmarks,
      creditArrangementsMap,
      sourceInstitutionsMap,
      isMobile,
    } = this.props;

    const creditItemsMap = this.transformBookmarksToCreditItemsMap(
      creditBookmarks,
      creditArrangementsMap,
    );

    const simpleCreditItemsMap = this.transformBookmarksToSimpleCreditItemsMap(
      creditBookmarks,
      creditArrangementsMap,
    );

    const targetCourseMap = this.transfromBookmarksToTargetCourseMap(
      creditBookmarks,
      creditArrangementsMap,
    );

    this.sortInsideTargetCourseMap(targetCourseMap);

    const itemsSortedByCourse = Object.keys(targetCourseMap)
      .sort((a, b) => {
        const blockA = targetCourseMap[a];
        const blockB = targetCourseMap[b];
        if (isEmpty(blockA) || isEmpty(blockB)) {
          return 0;
        }
        return generalCreditSortOrder(blockA[0], blockB[0]);
      })
      .map((sourceInstitutionID) => (
        <CourseCreditList
          key={sourceInstitutionID}
          credentials={targetCourseMap[sourceInstitutionID]}
          isMobile={isMobile}
          selectedSourceInstitution={sourceInstitutionsMap[sourceInstitutionID]}
          creditBookmarks={creditBookmarks}
        />
      ));

    const itemsSortedByUni = Object.keys(simpleCreditItemsMap)
      .sort((a, b) => this.sortForUniGroups(a, b, simpleCreditItemsMap))
      .map((sourceInstitutionID) => {
        const creditItems = creditItemsMap[sourceInstitutionID] || [];
        //fix order when adding new things
        return (
          <div
            key={sourceInstitutionID}
            className={classes.groupedCreditContainer}
            style={isMobile ? {} : { margin: "18px 0" }}
          >
            <div
              className={`${classes.sourceInstitutionLabel} source-institution-label`}
            >
              {sourceInstitutionID}
            </div>
            {isMobile && <div className={classes.mobileLineSeparator}></div>}
            {simpleCreditItemsMap[sourceInstitutionID].error ? (
              <div className={classes.savedItems}>
                No credit items found for this institution!
              </div>
            ) : (
              <div className={classes.savedItems}>
                {creditItems
                  .sort((a, b) => {
                    const aToCompare = a.grouped
                      ? a.grouped.sort(generalCreditSortOrder)[0]
                      : a;
                    const bToCompare = b.grouped
                      ? b.grouped.sort(generalCreditSortOrder)[0]
                      : b;
                    if (a.grouped && !b.grouped) {
                      return -1;
                    }
                    if (!a.grouped && b.grouped) {
                      return 1;
                    }
                    return generalCreditSortOrder(aToCompare, bToCompare);
                  })
                  .map((creditItem, index) => {
                    if (creditItem.grouped) {
                      return (
                        <ArticulationCreditList
                          key={index}
                          groupedArticulations={creditItem}
                          articulations={creditItem}
                          selectedSourceInstitution={
                            sourceInstitutionsMap[sourceInstitutionID]
                          }
                          creditBookmarks={creditBookmarks}
                          isMobile={isMobile}
                        />
                      );
                    }
                    return isArticulation(creditItem.type) ? (
                      <ArticulationCreditList
                        key={index}
                        groupedArticulations={creditItem}
                        articulations={creditItem}
                        selectedSourceInstitution={
                          sourceInstitutionsMap[sourceInstitutionID]
                        }
                        creditBookmarks={creditBookmarks}
                        isMobile={isMobile}
                      />
                    ) : (
                      <PrecedentCreditList
                        key={index}
                        precedents={[creditItem]}
                        selectedSourceInstitution={
                          sourceInstitutionsMap[sourceInstitutionID]
                        }
                        creditBookmarks={creditBookmarks}
                        isMobile={isMobile}
                      />
                    );
                  })}
              </div>
            )}
          </div>
        );
      });

    if (this.state.groupedByCourse) {
      return itemsSortedByCourse;
    } else {
      return itemsSortedByUni;
    }
  };

  render() {
    const { creditBookmarks, isMobile } = this.props;

    if (isEmpty(creditBookmarks)) {
      return (
        <>
          <Row className="title-section">
            <Col sm={{ span: 0 }} md={{ span: 1 }} lg={{ span: 3 }} />
            <Col sm={{ span: 24 }} md={{ span: 22 }} lg={{ span: 18 }}>
              <div className={`classes.noSavedCreditContainer`}>
                <NoSavedCredit />
              </div>
            </Col>
          </Row>
          <div
            id={isMobile ? "saved-section-mobile" : "saved-section"}
            style={{ marginTop: "18px" }}
          >
            <Row>
              <Col sm={{ span: 0 }} md={{ span: 1 }} lg={{ span: 3 }} />
              <Col sm={{ span: 24 }} md={{ span: 22 }} lg={{ span: 18 }}>
                <div className={classes.navContainer}>
                  <BackToResultButton isMobile={isMobile} />
                </div>
              </Col>
            </Row>
          </div>
        </>
      );
    }

    return (
      <div className={classes.savedCreditContainer}>
        <Row className="title-section">
          <Col sm={{ span: 0 }} md={{ span: 1 }} lg={{ span: 3 }} />
          <Col sm={{ span: 24 }} md={{ span: 22 }} lg={{ span: 18 }}>
            <div>
              <div className={`${classes.title} page-title`}>SAVED</div>
              {i
                .t("saved.description", { returnObjects: true })
                .map((li, i) => (
                  <p key={`${i}${li}`}>{li}</p>
                ))}
            </div>
          </Col>
        </Row>

        {!this.props.applicationHasLoaded ? (
          <FullPageSpinner />
        ) : (
          <div id={isMobile ? "saved-section-mobile" : "saved-section"}>
            <Row>
              <Col sm={{ span: 0 }} md={{ span: 1 }} lg={{ span: 3 }} />
              <Col sm={{ span: 24 }} md={{ span: 22 }} lg={{ span: 18 }}>
                <div className={classes.navContainer}>
                  <BackToResultButton isMobile={isMobile} />
                  <div className={classes.switchContainer}>
                    <span style={{ marginRight: 10 }}>
                      Group by {i.t("institution")} course
                    </span>
                    <Switch
                      onChange={this.changeGrouping}
                      checked={this.state.groupedByCourse}
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                    />
                  </div>
                </div>
                {this.renderSavedItems()}
              </Col>
            </Row>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    creditBookmarks: getCreditBookmarks(state),
    creditArrangementsMap: getCreditArrangementsMap(state),
    isFetchingSourceInstitutions: isFetchingSourceInstitutions(state),
    isFetchingCreditArrangements: isFetchingCreditArrangements(state),
    sourceInstitutionsMap: getSourceInstitutionsMap(state),
    applicationHasLoaded: isApplicationFinishedLoadingSelector(state),
  };
};

export default connect(mapStateToProps)(SavedCreditItems);
